/**
 * 远程控制 Socket 管理模块，负责初始化连接与全局监听。
 */
import { io } from "socket.io-client";
import { create } from "zustand";
import { handleRoomMessage } from "@/services/remote/signaling";
import { getValidGuestToken } from "@/services/remote/token";
import { SIGNAL_SOCKET_PATH, SIGNAL_SOCKET_URL } from "./config";

console.log(
	"SIGNAL_SOCKET_URL:",
	SIGNAL_SOCKET_URL,
	"path:",
	SIGNAL_SOCKET_PATH,
);

export const socket = io(SIGNAL_SOCKET_URL, {
	autoConnect: false,
	path: SIGNAL_SOCKET_PATH,
});

type SocketStoreState = {
	isConnected: boolean;
	token: string | null;
	connect: () => void;
	disconnect: () => void;
	setConnected: (status: boolean) => void;
	setToken: (token: string | null) => void;
};

// Socket 状态管理 store
export const useSocketStore = create<SocketStoreState>((set) => ({
	isConnected: false,
	token: null,

	connect: () => {
		socket.connect();
	},

	disconnect: () => {
		socket.disconnect();
	},

	setConnected: (status) => set({ isConnected: status }),
	setToken: (token) => set({ token }),
}));

// 全局初始化标志
let isInitializing = false;
let isInitialized = false;

// 初始化 socket 连接（包含 token 获取）
export const initializeSocket = async (): Promise<(() => void) | undefined> => {
	// 防止重复初始化。返回 noop cleanup 而非 undefined，
	// 让调用方（App.tsx）拿到的清理函数语义统一，不必区分"跳过"与"成功"。
	if (isInitialized || isInitializing) {
		console.log("Socket already initialized or initializing, skipping...");
		return () => {};
	}

	isInitializing = true;
	console.log("initialize socket");

	try {
		const token = await getValidGuestToken();
		console.log("got guest token:", token);

		if (!token) {
			console.warn("guest token missing, abort initialize");
			isInitializing = false;
			return () => {};
		}

		// 设置 token 到 store
		useSocketStore.getState().setToken(token);

		// 使用 token 连接 socket
		socket.auth = { token };
		socket.connect();

		// 设置事件监听
		const cleanup = setupGlobalSocketListeners();
		isInitialized = true;
		isInitializing = false;

		// 返回清理方法，便于重新初始化
		return () => {
			cleanup?.();
			isInitialized = false;
			isInitializing = false;
		};
	} catch (error) {
		console.error("Failed to get token:", error);
		isInitializing = false;
		return () => {};
	}
};

// 设置全局 socket 事件监听
export const setupGlobalSocketListeners = () => {
	console.log("setup global socket listeners");

	const { setConnected } = useSocketStore.getState();

	socket.on("connect", () => {
		setConnected(true);
		console.log("Socket connected and ready for signaling");
	});

	socket.on("disconnect", () => {
		setConnected(false);
	});

	socket.on("room-message", (data) => {
		handleRoomMessage(data);
	});

	return () => {
		socket.off("room-message");
		socket.off("connect");
		socket.off("disconnect");
	};
};

/**
 * 等待 socket 完成连接，确保后续信令操作可用
 * @param {number} timeoutMs 超时时间（毫秒）
 * @returns {Promise<void>} 连接成功时 resolve，超时或失败时 reject
 */
export const waitForSocketConnection = (timeoutMs = 15000) => {
	return new Promise<void>((resolve, reject) => {
		if (!socket) {
			reject(new Error("Socket instance not initialized"));
			return;
		}

		if (socket.connected) {
			resolve();
			return;
		}

		const auth = socket.auth as { token?: string } | undefined;
		if (socket.disconnected && auth?.token) {
			socket.connect();
		}

		let settled = false;
		const cleanup = () => {
			socket.off("connect", handleConnect);
			socket.off("connect_error", handleError);
			socket.off("error", handleError);
			if (timer) {
				clearTimeout(timer);
			}
		};

		const handleConnect = () => {
			if (settled) return;
			settled = true;
			cleanup();
			resolve();
		};

		const handleError = (err: Error) => {
			if (settled) return;
			settled = true;
			cleanup();
			const message = err?.message || "Socket connection failed";
			reject(new Error(message));
		};

		const timer = setTimeout(() => {
			if (settled) return;
			settled = true;
			cleanup();
			reject(new Error("Socket connection timeout"));
		}, timeoutMs);

		socket.once("connect", handleConnect);
		socket.once("connect_error", handleError);
		socket.once("error", handleError);
	});
};
